import { CashVarianceReport } from "../../services/signOnService";

const PRINT_FRAME_ID = "cash-variance-print-frame";

// Shown in the physical column where nothing is counted
const DASH = "&mdash;";

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const money = (value: number | null | undefined): string =>
    value == null
        ? DASH
        : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pad = (value: number) => String(value).padStart(2, "0");

const formatDateTime = (date: Date): string => {
    const hours = date.getHours();
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
        `${pad(hours % 12 || 12)}:${pad(date.getMinutes())} ${hours < 12 ? "AM" : "PM"}`;
};

// Server times arrive as "YYYY-MM-DD HH:MM:SS" in server-local time; show them as-is.
const formatServerDateTime = (value: string | null | undefined): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value || "");
    if (!match) {
        return value || "-";
    }
    const [, yyyy, mm, dd, hh, min] = match;
    return formatDateTime(new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min)));
};

const formatServerDate = (value: string | null | undefined): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value || "-";
};

const isBalanced = (variance: number) => Math.abs(variance) < 0.005;

const varianceClass = (variance: number | null): string =>
    variance == null || isBalanced(variance) ? "" : variance > 0 ? "excess" : "shortage";

// "SHORTAGE - Rs. 500.00" under the big variance figure
const varianceStatus = (variance: number | null): string => {
    if (variance == null) return "NOT DECLARED";
    if (isBalanced(variance)) return "BALANCED";
    return `${variance > 0 ? "EXCESS" : "SHORTAGE"} &ndash; Rs. ${money(Math.abs(variance))}`;
};

const generateCashVarianceReportHtml = (report: CashVarianceReport): string => {
    const { shift, company, invoice_summary: invoices, cash, card } = report;
    const printedDate = formatDateTime(new Date());

    const infoRow = (label: string, value: string) =>
        `<div class="info-row"><span class="info-label">${escapeHtml(label)}</span><span>: ${escapeHtml(value || "-")}</span></div>`;

    const amountRow = (label: string, system: string, physical: string, rowClass = "") => `
        <tr class="${rowClass}"><td>${escapeHtml(label)}</td><td class="num">${system}</td><td class="num">${physical}</td></tr>`;

    const varianceBlock = (label: string, variance: number | null) => `
        <div class="variance-block">
            <div class="variance-label">${escapeHtml(label)}</div>
            <div class="variance-figure ${varianceClass(variance)}">${money(variance)}</div>
            <div class="variance-status ${varianceClass(variance)}">${varianceStatus(variance)}</div>
        </div>`;

    const methodRows = report.methods
        .map(
            (method) => `
            <tr>
                <td>${escapeHtml(method.label)}</td>
                <td class="num">${money(method.system)}</td>
                <td class="num">${money(method.actual)}</td>
                <td class="num ${varianceClass(method.variance)}">${money(method.variance)}</td>
            </tr>`
        )
        .join("");

    const infoNote = report.info_lines.length
        ? `<div class="note">Not counted at sign-off: ${report.info_lines
            .map((line) => `${escapeHtml(line.label)} ${money(line.amount)}`)
            .join(" &nbsp;|&nbsp; ")}</div>`
        : "";

    const invoiceRange = invoices.first_invoice
        ? `${invoices.first_invoice} - ${invoices.last_invoice}`
        : "-";
    const invoiceCount = (count: number, amount: number) => `${count} (${money(amount)})`;

    return `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <title>Daily Cash Reconciliation</title>
            <style>
                @page { size: A4; margin: 10mm; }
                * { box-sizing: border-box; }
                html, body { margin: 0; }
                /* Content padding on top of @page margin: printers and the "Margins: None"
                   dialog option can drop @page margins and clip the edges. */
                body { font-family: Arial, sans-serif; padding: 4mm 6mm; color: #1a1a1a; font-size: 11px; overflow-wrap: anywhere; }
                .header-row { display: flex; justify-content: space-between; align-items: flex-start; }
                .company-name { font-weight: 700; font-size: 14px; }
                .title { font-weight: 700; font-size: 15px; margin-top: 4px; }
                .header-right { text-align: right; white-space: nowrap; }
                hr { border: none; border-top: 1px solid #333; margin: 8px 0; }
                .two-col { display: flex; gap: 24px; }
                .two-col > div { flex: 1; min-width: 0; }
                .info-row { display: flex; margin-bottom: 3px; }
                .info-label { width: 90px; flex-shrink: 0; }
                h3 { font-size: 12px; margin: 12px 0 4px; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #999; padding: 3px 6px; text-align: left; }
                th { background: #eee; }
                /* Fixed layout: the amount columns share the rest equally, so sections line up */
                .amount-table { table-layout: fixed; }
                .amount-table td:first-child, .amount-table th:first-child { width: 55%; }
                .num { text-align: right; white-space: nowrap; }
                .total td { font-weight: 700; }
                .excess { color: #0a6b2d; }
                .shortage { color: #b00020; }
                .variance-block { display: flex; align-items: center; gap: 16px; border: 1px solid #999; border-top: none; padding: 4px 6px; }
                .variance-label { width: 55%; }
                .variance-figure { font-size: 24px; font-weight: 700; min-width: 120px; text-align: center; }
                .variance-status { font-weight: 700; }
                .note { margin-top: 4px; font-size: 10px; color: #444; }
                .not-declared { margin-top: 6px; font-weight: 700; color: #b00020; }
                .declaration { break-inside: avoid; }
                .declaration-text { margin-bottom: 8px; }
                .approval-table { width: 70%; }
                .approval-table th, .approval-table td { border: none; }
                .approval-table th { background: none; text-align: center; }
                .approval-table td { height: 34px; vertical-align: bottom; }
                .signature-cell { width: 45%; text-align: center; }
                .signature-line { border-top: 1px solid #333; width: 70%; margin: 0 auto; }
                .printed-date { margin-top: 14px; }
            </style>
        </head>
        <body>
            <div class="header-row">
                <div>
                    <div class="company-name">${escapeHtml(company.name || "-")}</div>
                    <div>${escapeHtml(company.address || "")}</div>
                    <div class="title">Daily Cash Reconciliation</div>
                </div>
                <div class="header-right">
                    <div>Print Date: ${escapeHtml(printedDate)}</div>
                </div>
            </div>
            <hr />
            <div class="two-col">
                <div>
                    ${infoRow("Business Date", formatServerDate(shift.business_date))}
                    ${infoRow("Cashier", shift.user_name)}
                    ${infoRow("Counter", shift.station_id)}
                    ${infoRow("Branch", shift.branch_name ? `${shift.station_loc} - ${shift.branch_name}` : shift.station_loc || "-")}
                    ${infoRow("Shift No", shift.shift_no != null ? String(shift.shift_no) : "-")}
                    ${infoRow("Sign On", formatServerDateTime(shift.signon_time))}
                    ${infoRow("Sign Off", formatServerDateTime(shift.signoff_time))}
                </div>
                <div>
                    ${infoRow("Invoices", invoiceRange)}
                    ${infoRow("Sales", invoiceCount(invoices.sales_count, invoices.sales_total))}
                    ${infoRow("Returns", invoiceCount(invoices.return_count, invoices.return_total))}
                    ${infoRow("Cancelled", invoiceCount(invoices.cancelled_count, invoices.cancelled_total))}
                </div>
            </div>
            <hr />
            ${report.is_declared ? "" : `<div class="not-declared">Physical amounts were not declared at sign-off.</div>`}

            <h3>1. Cash Reconciliation</h3>
            <table class="amount-table">
                <thead><tr><th>Description</th><th class="num">System (Rs.)</th><th class="num">Physical (Rs.)</th></tr></thead>
                <tbody>
                    ${amountRow("Opening Cash", money(cash.opening), money(cash.opening))}
                    ${amountRow("Cash Sales", money(cash.sales), DASH)}
                    ${amountRow("Cash Refunds", money(cash.refunds), DASH)}
                    ${amountRow("Cash In", money(cash.cash_in), DASH)}
                    ${amountRow("Cash Out", money(cash.cash_out), DASH)}
                    ${amountRow("Expected Closing Cash", money(cash.expected), DASH, "total")}
                    ${amountRow("Cash Counted", DASH, money(cash.counted), "total")}
                </tbody>
            </table>
            ${varianceBlock("Cash Variance", cash.variance)}

            <h3>2. Credit Card Reconciliation</h3>
            <table class="amount-table">
                <thead><tr><th>Description</th><th class="num">System (Rs.)</th><th class="num">Physical / EDC (Rs.)</th></tr></thead>
                <tbody>
                    ${amountRow("Credit Card Sales", money(card.sales), money(card.sales_counted))}
                    ${amountRow("Credit Card Refunds", money(card.refunds), money(card.refunds_counted))}
                    ${amountRow("Expected Total", money(card.total), money(card.total_counted), "total")}
                </tbody>
            </table>
            ${varianceBlock("Credit Card Variance", card.variance)}

            <h3>3. Other Payment Reconciliation</h3>
            <table class="amount-table">
                <thead><tr><th>Payment Method</th><th class="num">System (Rs.)</th><th class="num">Actual (Rs.)</th><th class="num">Variance</th></tr></thead>
                <tbody>
                    ${methodRows}
                    <tr class="total">
                        <td>Total</td>
                        <td class="num">${money(report.methods_total.system)}</td>
                        <td class="num">${money(report.methods_total.actual)}</td>
                        <td class="num ${varianceClass(report.methods_total.variance)}">${money(report.methods_total.variance)}</td>
                    </tr>
                </tbody>
            </table>
            ${infoNote}

            <div class="declaration">
                <h3>4. Cashier Declaration</h3>
                <div class="declaration-text">I confirm that the physical cash and payment records have been counted and checked against the POS system closing balance.</div>
                <table class="approval-table">
                    <thead><tr><th>Approval</th><th class="signature-cell">Signature</th></tr></thead>
                    <tbody>
                        <tr><td>Cashier</td><td class="signature-cell"><div class="signature-line"></div></td></tr>
                        <tr><td>Supervisor</td><td class="signature-cell"><div class="signature-line"></div></td></tr>
                        <tr><td>Manager</td><td class="signature-cell"><div class="signature-line"></div></td></tr>
                    </tbody>
                </table>
                <div class="printed-date">Printed Date: ${escapeHtml(printedDate)}</div>
            </div>
        </body>
        </html>
    `;
};

// Prints from an off-screen iframe (like the POS bill), so it works after an
// async sign-off call without tripping the popup blocker.
export const printCashVarianceReport = (report: CashVarianceReport, onError?: (message: string) => void) => {
    document.getElementById(PRINT_FRAME_ID)?.remove();

    const frame = document.createElement("iframe");
    frame.id = PRINT_FRAME_ID;
    frame.setAttribute("aria-hidden", "true");
    frame.tabIndex = -1;
    Object.assign(frame.style, {
        position: "fixed",
        left: "-10000px",
        top: "0",
        width: "21cm",
        height: "600px",
        border: "0",
    });

    frame.onload = () => {
        const frameWindow = frame.contentWindow;
        if (!frameWindow) {
            frame.remove();
            onError?.("Failed to prepare the print document.");
            return;
        }
        frameWindow.addEventListener("afterprint", () => setTimeout(() => frame.remove(), 0));
        try {
            frameWindow.focus();
            frameWindow.print();
        } catch {
            frame.remove();
            onError?.("Failed to open the print dialog.");
        }
    };

    frame.srcdoc = generateCashVarianceReportHtml(report);
    document.body.appendChild(frame);
};
